import { Sun, Moon } from "lucide-react";
import { usePanelMode } from "../hooks/usePanelMode";

/**
 * Light / Dark panel switch for a chrome panel header.
 *
 * Lives in chrome, so it is styled from chrome tokens and looks identical in
 * both modes — it must not restyle itself out from under the click that just
 * changed the mode.
 *
 * The label names the action rather than the state ("Dark panel" = go dark),
 * matching the design handoff. The icon shows the destination for the same
 * reason. On mobile it collapses to the icon and keeps a 44px tap target.
 */
export function PanelModeToggle({ isMobile = false }) {
  const { panelMode, setPanelMode } = usePanelMode();
  const goingDark = panelMode !== "dark";
  const label = goingDark ? "Dark panel" : "Light panel";
  const Icon = goingDark ? Moon : Sun;

  return (
    <button
      onClick={() => setPanelMode(goingDark ? "dark" : "light")}
      title={`Switch to ${label.toLowerCase()}`}
      aria-label={`Switch to ${label.toLowerCase()}`}
      style={{
        display: "flex", alignItems: "center", gap: 7, flexShrink: 0,
        padding: isMobile ? 0 : "7px 12px",
        minWidth: isMobile ? 44 : undefined,
        minHeight: isMobile ? 44 : undefined,
        justifyContent: "center",
        borderRadius: 8,
        background: "transparent",
        border: isMobile ? "none" : "1px solid var(--chrome-border)",
        color: "var(--chrome-text-secondary)",
        fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit",
        transition: "color 0.15s, border-color 0.15s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = "var(--chrome-text)";
        if (!isMobile) e.currentTarget.style.borderColor = "var(--chrome-border-strong)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = "var(--chrome-text-secondary)";
        if (!isMobile) e.currentTarget.style.borderColor = "var(--chrome-border)";
      }}
    >
      <Icon size={14} />
      {!isMobile && label}
    </button>
  );
}
